:host {
  --chat-ink: #181916;
  --chat-paper: #fbfaf5;
  --chat-muted: #6a6d63;
  --chat-line: #d8c99e;
  --chat-accent: #efe1bb;
  --chat-accent-strong: color-mix(in srgb, var(--chat-accent) 58%, var(--chat-ink));
  --chat-radius: 0.75rem;
  color: var(--chat-ink);
  font-family:
    Inter,
    ui-sans-serif,
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
}

* {
  box-sizing: border-box;
}
button,
textarea {
  font: inherit;
}
button {
  color: inherit;
}

.launcher-shell {
  bottom: 1rem;
  position: fixed;
  right: 1rem;
  z-index: 2147483647;
}
.launcher-shell::before {
  bottom: 0;
  content: '';
  height: 100%;
  position: absolute;
  right: 100%;
  width: 0.75rem;
}
.launcher {
  align-items: center;
  background: var(--chat-accent);
  border: 0;
  border-radius: 999px;
  box-shadow: 0 0.35rem 1rem rgb(0 0 0 / 14%);
  color: white;
  cursor: pointer;
  display: flex;
  font-weight: 700;
  justify-content: center;
  overflow: visible;
  position: relative;
}

.launcher-agent-button {
  height: var(--chat-launcher-size, 4rem);
  padding: 0;
  width: var(--chat-launcher-size, 4rem);
}
.launcher-text-button {
  border-radius: var(--chat-radius);
  min-height: 3rem;
  padding: 0 1.1rem;
}
.launcher-agent,
.launcher-image {
  border-radius: inherit;
  display: block;
  height: 100%;
  width: 100%;
}
.launcher-image {
  object-fit: cover;
}
.launcher-agent {
  background: var(--chat-accent);
  padding: 0.35rem;
}
.launcher-agent svg {
  animation: agent-float 3.2s ease-in-out infinite;
  display: block;
  height: 100%;
  overflow: visible;
  transform-box: fill-box;
  transform-origin: center;
  width: 100%;
}
.launcher-tooltip {
  background: white;
  border: 1px solid var(--chat-line);
  border-radius: var(--chat-radius);
  bottom: 0;
  box-shadow: 0 0.3rem 1rem rgb(0 0 0 / 12%);
  color: var(--chat-ink);
  cursor: zoom-in;
  display: grid;
  gap: 0;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  right: calc(100% + 0.75rem);
  text-align: left;
  transform: translateX(0.25rem);
  transition:
    opacity 150ms ease,
    transform 150ms ease,
    visibility 150ms ease,
    width 280ms ease;
  visibility: hidden;
  white-space: normal;
  width: min(15rem, calc(100vw - var(--chat-launcher-size, 4rem) - 3rem));
}
.launcher-tooltip.is-expanded {
  cursor: zoom-out;
  width: min(44rem, calc(100vw - var(--chat-launcher-size, 4rem) - 3rem));
}
.launcher-tooltip-artwork-frame {
  aspect-ratio: 3 / 2;
  display: block;
  position: relative;
}
.launcher-tooltip-artwork {
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
}
.launcher-tooltip-copy {
  background: var(--chat-accent);
  color: var(--chat-ink);
  display: grid;
  gap: 0.2rem;
  padding: 0.72rem 0.9rem 0.68rem;
}
.launcher-tooltip-title {
  font-size: 0.78rem;
  font-style: italic;
  font-weight: 800;
  line-height: 1.3;
  text-align: center;
  transition: font-size 280ms ease;
}
.launcher-tooltip-credit {
  color: var(--chat-muted);
  font-size: 0.44rem;
  font-weight: 400;
  line-height: 1.35;
  bottom: 0.25rem;
  position: absolute;
  right: 0.35rem;
  text-align: right;
  text-shadow: 0 1px 1px rgb(255 255 255 / 80%);
  white-space: nowrap;
}
.launcher-tooltip-expand {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 0.45rem;
  cursor: pointer;
  display: flex;
  height: 2rem;
  justify-content: center;
  left: 0.5rem;
  opacity: 0;
  padding: 0;
  position: absolute;
  top: 0.5rem;
  transform: scale(0.9);
  transition:
    opacity 150ms ease,
    transform 150ms ease;
  width: 2rem;
}
.launcher-tooltip-expand:hover {
  background: transparent;
}
.launcher-tooltip-expand svg {
  filter: drop-shadow(0 1px 1px rgb(255 255 255 / 85%));
  fill: none;
  height: 1rem;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 2;
  width: 1rem;
}
.launcher-tooltip:hover .launcher-tooltip-expand,
.launcher-tooltip-expand:focus-visible {
  opacity: 1;
  transform: scale(1);
}
.launcher-tooltip.is-expanded .launcher-tooltip-title {
  font-size: 1.1rem;
}
.launcher-tooltip.is-expanded .launcher-tooltip-credit {
  font-size: 0.65rem;
}
.launcher-shell:not(.suppress-tooltip):hover > .launcher[aria-expanded='false'] + .launcher-tooltip,
.launcher-shell:not(.suppress-tooltip):focus-within
  > .launcher[aria-expanded='false']
  + .launcher-tooltip {
  opacity: 1;
  pointer-events: auto;
  transform: translateX(0);
  visibility: visible;
}
.agent-antenna,
.agent-smile {
  fill: none;
  stroke: var(--chat-ink);
  stroke-linecap: round;
  stroke-width: 2.5;
}
.agent-signal,
.agent-head {
  fill: var(--chat-ink);
}
.agent-signal {
  animation: agent-signal 1.8s ease-in-out infinite;
  transform-box: fill-box;
  transform-origin: center;
}
.agent-face {
  fill: var(--chat-paper);
}
.agent-eye {
  animation: agent-blink 4.5s ease-in-out infinite;
  fill: var(--chat-ink);
  transform-box: fill-box;
  transform-origin: center;
}
@keyframes agent-float {
  0%,
  100% {
    transform: translateY(0) rotate(-1deg);
  }
  50% {
    transform: translateY(-0.15rem) rotate(1deg);
  }
}
@keyframes agent-signal {
  0%,
  100% {
    opacity: 0.75;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1.15);
  }
}
@keyframes agent-blink {
  0%,
  45%,
  50%,
  100% {
    transform: scaleY(1);
  }
  47.5% {
    transform: scaleY(0.12);
  }
}

.panel {
  background: var(--chat-paper);
  border: 1px solid var(--chat-line);
  border-radius: var(--chat-radius);
  bottom: 4.75rem;
  box-shadow: 0 1.25rem 4rem rgb(0 0 0 / 18%);
  display: grid;
  grid-template-rows: auto minmax(10rem, 1fr) auto auto auto;
  max-height: min(42rem, calc(100vh - 6rem));
  overflow: hidden;
  position: fixed;
  right: 1rem;
  width: min(24rem, calc(100vw - 2rem));
  z-index: 2147483646;
}

.panel[hidden] {
  display: none;
}
.launcher-shell-agent + .panel {
  bottom: calc(1rem + var(--chat-launcher-size, 4rem) + 0.75rem);
}
header {
  align-items: center;
  background: var(--chat-accent);
  border-bottom: 1px solid var(--chat-line);
  color: var(--chat-ink);
  display: flex;
  justify-content: space-between;
  padding: 0.6rem 0.75rem;
}
.header-copy {
  align-items: center;
  display: flex;
  gap: 0.65rem;
  min-width: 0;
}
.header-copy strong {
  font-size: 0.95rem;
}
.header-live-dot {
  animation: live-pulse 2.8s ease-in-out infinite;
  background: var(--chat-accent-strong);
  border: 2px solid var(--chat-paper);
  border-radius: 50%;
  box-shadow: 0 0 0 1px color-mix(in srgb, var(--chat-accent-strong) 36%, transparent);
  flex: 0 0 auto;
  height: 0.9rem;
  width: 0.9rem;
}
@keyframes live-pulse {
  0%,
  100% {
    box-shadow: 0 0 0 1px color-mix(in srgb, var(--chat-accent-strong) 36%, transparent);
  }
  50% {
    box-shadow: 0 0 0 0.4rem color-mix(in srgb, var(--chat-accent-strong) 12%, transparent);
  }
}
.header-actions {
  align-items: center;
  display: flex;
  gap: 0.25rem;
}
.clear,
.close {
  background: transparent;
  border: 0;
  color: var(--chat-ink);
  cursor: pointer;
  border-radius: var(--chat-radius);
  height: 1.9rem;
  padding: 0 0.45rem;
  transition:
    background-color 150ms ease,
    color 150ms ease;
}
.clear {
  font-size: 0.75rem;
}
.close {
  aspect-ratio: 1;
  display: grid;
  place-items: center;
}
.close-icon {
  display: block;
  fill: none;
  height: 0.9rem;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-width: 2.5;
  width: 0.9rem;
}
.header-actions button:hover:not(:disabled) {
  background: rgb(24 25 22 / 10%);
  color: var(--chat-ink);
}
.messages {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  overflow: auto;
  padding: 1rem;
}
.welcome {
  color: var(--chat-muted);
  margin: auto;
  max-width: 15rem;
  text-align: center;
}
.message {
  border-radius: 0.75rem;
  line-height: 1.45;
  margin: 0;
  max-width: 86%;
  overflow-wrap: anywhere;
  padding: 0.65rem 0.8rem;
  white-space: pre-wrap;
}
.message.user {
  align-self: flex-end;
  background: var(--chat-accent);
  color: var(--chat-ink);
}
.message.assistant {
  align-self: flex-start;
  background: transparent;
  padding-inline: 0.1rem;
}
.status {
  color: var(--chat-muted);
  font-size: 0.75rem;
  margin: 0;
  min-height: 1.5rem;
  padding: 0.15rem 1rem;
}

form {
  align-items: end;
  border-top: 1px solid var(--chat-line);
  display: grid;
  gap: 0.5rem;
  grid-template-columns: 1fr auto;
  padding: 0.75rem;
}
label {
  height: 1px;
  margin: -1px;
  overflow: hidden;
  position: absolute;
  width: 1px;
}
textarea {
  background: var(--chat-paper);
  border: 1px solid var(--chat-line);
  border-radius: 0.5rem;
  color: var(--chat-ink);
  max-height: 8rem;
  min-height: 2.65rem;
  padding: 0.65rem 0.75rem;
  resize: vertical;
}
.send {
  background: var(--chat-accent);
  border: 0;
  border-radius: var(--chat-radius);
  cursor: pointer;
  font-weight: 800;
  min-height: 2.65rem;
  padding: 0 0.85rem;
  transition: background-color 150ms ease;
}
.send:hover:not(:disabled) {
  background: color-mix(in srgb, var(--chat-accent) 72%, white);
}
button:disabled,
textarea:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
button:focus-visible {
  outline: 2px solid #5b74ff;
  outline-offset: 2px;
}
textarea:focus-visible {
  border-color: var(--chat-ink);
  box-shadow: inset 0 0 0 1px var(--chat-ink);
  outline: none;
}
small {
  color: var(--chat-muted);
  font-size: 0.68rem;
  padding: 0 0.75rem 0.7rem;
  text-align: center;
}

@media (max-width: 30rem) {
  .panel {
    border-radius: 0;
    bottom: 0;
    max-height: none;
    right: 0;
    top: 0;
    width: 100vw;
  }
  .launcher-shell-agent + .panel {
    bottom: 0;
  }
  .launcher-shell:has(.launcher[aria-expanded='true']) {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  * {
    animation: none !important;
    scroll-behavior: auto !important;
    transition: none !important;
  }
}
